// Quick Thrift mode — single-photo capture flow optimized for the in-store
// buy-decision moment. Take 1 photo, hit scan, get a brand/era/rough-price
// read in ~3-5s. No category picker, no brand input, no multi-slot — speed
// is the entire point.
//
// Mirrors the placeholder-insert + fire-and-forget pattern from Analyze.tsx:
// inserts a scans row with is_quick_post: true, navigates immediately to
// /result?scanId=...&mode=quick, then invokes fig-verify with mode: "quick"
// which writes the result back to the same row when done.

import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "@/components/primitives";
import { MobileFrame } from "@/components/MobileFrame";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "@/hooks/use-toast";
import {
  disposeLocalPhoto,
  fileToAnalyzeDataUrl,
  makeLocalPhoto,
  normalizeHeic,
  uploadPhotos,
  type LocalPhoto,
} from "@/lib/photos";
import { isNative, pickNativePhoto, dataUrlToFile } from "@/lib/nativeCamera";
import { tap } from "@/lib/nativeHaptics";
import { PhotoSourcePicker } from "@/components/PhotoSourcePicker";

function splitDataUrl(dataUrl: string): { media_type: string; data: string } {
  const m = /^data:([^;]+);base64,(.*)$/.exec(dataUrl || "");
  if (m) return { media_type: m[1] || "image/jpeg", data: m[2] };
  return { media_type: "image/jpeg", data: dataUrl };
}

export default function ScanQuick() {
  const nav = useNavigate();
  const { user } = useAuth();

  const [photo, setPhoto] = useState<LocalPhoto | undefined>(undefined);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    return () => {
      if (photo) disposeLocalPhoto(photo);
    };
  }, [photo]);

  const handleFile = async (file: File | null) => {
    if (!file) return;
    try {
      const normalized = await normalizeHeic(file);
      const local = await makeLocalPhoto(normalized);
      if (photo) disposeLocalPhoto(photo);
      setPhoto(local);
    } catch (e) {
      console.error("photo prep failed", e);
      toast({
        title: "Couldn't read that photo",
        description: "Try another shot.",
        variant: "destructive",
      });
    }
  };

  const onAddPhoto = async () => {
    if (busy) return;
    void tap();
    if (isNative) {
      setPickerOpen(true);
    } else {
      inputRef.current?.click();
    }
  };

  const onPickerChoice = async (source: "camera" | "library") => {
    setPickerOpen(false);
    try {
      const result = await pickNativePhoto(source);
      if (!result) return;
      const file = dataUrlToFile(result.dataUrl, "photo.jpg", result.mimeType);
      await handleFile(file);
    } catch (e) {
      console.error("native pick failed", e);
    }
  };

  const onRetake = () => {
    if (busy) return;
    void tap();
    if (photo) disposeLocalPhoto(photo);
    setPhoto(undefined);
  };

  const onScan = async () => {
    if (busy || !photo || !user) return;
    setBusy(true);
    void tap();
    try {
      // Encode photo to base64 for fig-verify.
      const dataUrl = await fileToAnalyzeDataUrl(photo.file);
      const { media_type, data } = splitDataUrl(dataUrl);

      // Upload the photo to storage so the result row has a viewable image.
      // (Full mode does this via scanStore; quick mode uploads directly.)
      const sessionId = `quick-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const uploaded = await uploadPhotos(user.id, sessionId, [photo]);
      const storageUrl = uploaded[0] ?? "";

      // Insert placeholder scans row marked as quick-mode.
      const { data: placeholder, error: phErr } = await supabase
        .from("scans")
        .insert({
          user_id: user.id,
          title: "Quick scan",
          verdict: "FAIL",
          img: storageUrl || "olive",
          photos: storageUrl ? [storageUrl] : [],
          processing_status: "processing",
          review_status: "approved_closet",
          status: "kept",
          is_quick_post: true,
        } as never)
        .select("id")
        .single();

      if (phErr || !placeholder?.id) {
        console.error("quick placeholder insert failed", phErr);
        throw new Error("Couldn't start quick scan");
      }

      const scanId = placeholder.id;
      sessionStorage.setItem("fig_current_scan_id", scanId);

      // Navigate immediately — fig-verify continues in the background.
      nav(`/result?scanId=${scanId}&mode=quick`, { replace: true });

      // Fire-and-forget the quick-mode fig-verify call.
      void supabase.functions
        .invoke("fig-verify", {
          body: {
            photos: [{ label: "Item", data, media_type }],
            scanId,
            mode: "quick",
          },
        })
        .then(({ error }) => {
          if (error) {
            console.error("fig-verify quick failed", error);
          }
        });
    } catch (e) {
      console.error("quick scan failed", e);
      const msg = e instanceof Error ? e.message : "Quick scan failed";
      toast({
        title: "Couldn't scan",
        description: msg,
        variant: "destructive",
      });
      setBusy(false);
    }
  };

  return (
    <MobileFrame>
      <div
        className="paper-bg"
        style={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          padding: "16px 16px 32px",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 20,
          }}
        >
          <button
            onClick={() => nav("/home")}
            aria-label="Back to home"
            style={{
              background: "transparent",
              border: "none",
              padding: 8,
              cursor: "pointer",
              color: "hsl(var(--ink))",
            }}
          >
            {Icon.chev(22, "l")}
          </button>
          <div
            className="caps"
            style={{
              color: "hsl(var(--rust))",
              letterSpacing: "0.14em",
              fontSize: 11,
            }}
          >
            Quick Thrift
          </div>
          <div style={{ width: 38 }} />
        </div>

        {/* Headline */}
        <div
          style={{
            fontFamily: "'Fraunces', 'Playfair Display', serif",
            fontSize: 24,
            fontWeight: 700,
            color: "hsl(var(--ink))",
            lineHeight: 1.2,
            marginBottom: 6,
          }}
        >
          One photo. Fast read.
        </div>
        <div
          style={{
            fontFamily: "'Fraunces', 'Playfair Display', serif",
            fontSize: 14,
            color: "hsl(var(--ink-2))",
            lineHeight: 1.4,
            marginBottom: 22,
          }}
        >
          Snap the item — get brand, era, and a rough price range. Use Full Verify after you buy it.
        </div>

        {/* Photo slot */}
        <div
          style={{
            width: "100%",
            aspectRatio: "4/3",
            border: "1px dashed hsl(var(--ink))",
            background: photo ? "hsl(var(--paper-3))" : "hsl(var(--cream))",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
            position: "relative",
            marginBottom: 14,
          }}
        >
          {photo ? (
            <>
              <img
                src={photo.previewUrl}
                alt="Selected"
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
              {!busy && (
                <button
                  onClick={onRetake}
                  className="caps"
                  style={{
                    position: "absolute",
                    bottom: 10,
                    right: 10,
                    padding: "6px 12px",
                    background: "hsl(var(--ink))",
                    color: "hsl(var(--ink-fg))",
                    border: "none",
                    borderRadius: 3,
                    fontSize: 10,
                    letterSpacing: "0.12em",
                    cursor: "pointer",
                  }}
                >
                  Retake
                </button>
              )}
            </>
          ) : (
            <button
              onClick={onAddPhoto}
              disabled={busy}
              style={{
                background: "transparent",
                border: "none",
                cursor: busy ? "default" : "pointer",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 10,
                color: "hsl(var(--ink-2))",
                padding: 20,
              }}
            >
              {Icon.camera(36)}
              <div
                className="caps"
                style={{ letterSpacing: "0.14em", fontSize: 11 }}
              >
                Add photo
              </div>
            </button>
          )}
        </div>

        {/* Hint */}
        <div
          style={{
            fontFamily: "'Fraunces', 'Playfair Display', serif",
            fontSize: 12,
            color: "hsl(var(--ink-2))",
            opacity: 0.8,
            marginBottom: 22,
            textAlign: "center",
          }}
        >
          Best: full piece visible, decent light, label in frame if possible.
        </div>

        {/* Scan CTA */}
        <button
          onClick={onScan}
          disabled={!photo || busy}
          style={{
            width: "100%",
            padding: "16px 22px",
            background: !photo || busy ? "hsl(var(--ink-3))" : "hsl(var(--ink))",
            color: "hsl(var(--ink-fg))",
            border: "none",
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 13,
            fontWeight: 600,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            cursor: !photo || busy ? "default" : "pointer",
          }}
        >
          {busy ? "Scanning…" : "Quick Scan"}
        </button>

        <button
          onClick={() => nav("/scan")}
          disabled={busy}
          style={{
            marginTop: 14,
            background: "transparent",
            border: "none",
            color: "hsl(var(--rust))",
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 11,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            cursor: busy ? "default" : "pointer",
          }}
        >
          Want full authentication? →
        </button>

        {/* Hidden file input for non-native fallback */}
        <input
          ref={(el) => {
            inputRef.current = el;
          }}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={(e) => {
            const f = e.target.files?.[0] ?? null;
            void handleFile(f);
            if (e.target) e.target.value = "";
          }}
          style={{ display: "none" }}
        />

        <PhotoSourcePicker
          open={pickerOpen}
          onCamera={() => void onPickerChoice("camera")}
          onLibrary={() => void onPickerChoice("library")}
          onCancel={() => setPickerOpen(false)}
        />
      </div>
    </MobileFrame>
  );
}
